// Pure helpers for turning a column's active filter value into readable text
// for the column-header tooltip. Kept free of React/rendering concerns so they
// can be unit-tested directly; the rendering lives in TableComponents/Table.jsx.

// Multi-select filters in this codebase store their value as an array of the
// raw option objects. We don't know which field is "the label", so try the
// conventional label keys first, then fall back to the column id (the pattern
// used by columns whose getOptionLabel is `option => option[column.id]`).
const LABEL_KEYS = ['label', 'name', 'displayname']
const extractOptionLabel = (obj, columnId) => {
  for (const key of LABEL_KEYS) {
    if (obj[key] != null && obj[key] !== '') return String(obj[key])
  }
  if (columnId && obj[columnId] != null && obj[columnId] !== '') {
    return String(obj[columnId])
  }
  return null
}

// The project's tooltip manager (Application/sagas/tooltipSagas.js) renders
// the data-title via innerHTML, so any '<' or '>' in our text would be parsed
// as HTML and dropped (e.g. literal '<none>' from a filter value vanishes).
// Escape characters so they survive.
export const escapeForTooltipHtml = s =>
  String(s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))

// True when a filter value represents an effectively-unfiltered state, i.e.
// the user has nothing meaningful filtering the column. TanStack already
// auto-removes `undefined` and `''` filter values from columnFilters, but
// object/array filters do not get that treatment: clearing a date-range filter
// calls onChange({}) (see XtractTableUtils.XtractDateRangePicker), which leaves
// an empty object in columnFilters so getIsFiltered() stays true. Treat such
// empty containers as cleared so the header indicator and badge count clear.
//
// Primitives (including '') are intentionally NOT treated as empty here: a bare
// value left in columnFilters was set deliberately (e.g. a single-select that
// stores '' to mean "<none>"), so it should still read as an active filter.
export const isFilterValueEmpty = v => {
  if (v == null) return true
  if (Array.isArray(v)) return !v.some(x => x != null && x !== '')
  if (typeof v === 'object') {
    return !Object.values(v).some(val => val != null && val !== '')
  }
  return false
}

// Render a filter value as readable text for a column-header tooltip.
// Handles plain values, arrays of primitives (text filters), arrays of option
// objects (multi-select filters), and object filters like {from, to} date
// ranges produced by custom Filter components.
export const formatFilterValue = (v, columnId) => {
  if (v == null) return ''
  if (Array.isArray(v)) {
    const items = v
      .filter(x => x != null && x !== '')
      .map(x => typeof x === 'object' ? extractOptionLabel(x, columnId) : String(x))
      .filter(x => x != null)
    return items.length > 0 ? items.join(', ') : `${v.length} selected`
  }
  if (typeof v === 'object') {
    return Object.entries(v)
      .filter(([, val]) => val != null && val !== '')
      .map(([key, val]) => `${key}: ${val}`)
      .join(', ')
  }
  return String(v)
}
